import React from 'react';
import { Link } from 'react-router-dom';
import { 
  Truck, 
  Shield, 
  CheckCircle, 
  AlertTriangle, 
  Scale,
  FileText,
  Users,
  DollarSign,
  Lock
} from 'lucide-react';

export default function TermosDeUso() {
  const sections = [
    {
      number: "01",
      title: "Natureza da BoraFrete",
      icon: Truck,
      content: (
        <>
          <p className="mb-4">
            A <strong>BoraFrete</strong> é uma <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-bold bg-blue-500/20 text-blue-400 border border-blue-500/30">Transportadora</span> que opera como <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-bold bg-green-500/20 text-green-400 border border-green-500/30">Marketplace de Cargas</span>.
          </p>
          
          <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4 mb-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="text-amber-400 font-semibold text-sm mb-1">Importante</h4>
                <p className="text-slate-300 text-sm">
                  Nossos clientes <strong className="text-white">NÃO SÃO OBRIGADOS</strong> a carregar diretamente com a BoraFrete. 
                  Atuamos como plataforma de conexão, não como intermediários obrigatórios.
                </p>
              </div>
            </div>
          </div>

          <p className="text-slate-300">
            A BoraFrete limita-se a <strong className="text-white">licenciar o uso da plataforma</strong> para que Usuários, por conta própria, 
            ofereçam e contratem diretamente serviços de transporte, <strong className="text-white">inexistindo qualquer participação da BoraFrete</strong> nas relações contratuais.
          </p>
        </>
      )
    },
    {
      number: "02",
      title: "Modelo Zero Mensalidade",
      icon: DollarSign,
      content: (
        <>
          <p className="mb-4">A BoraFrete adota um modelo transparente e justo:</p>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-4 text-center">
              <Users className="w-8 h-8 text-brand-500 mx-auto mb-2" />
              <h4 className="text-white font-semibold text-sm mb-1">Motoristas</h4>
              <p className="text-slate-400 text-xs">Sem mensalidade<br/>Pague apenas pelo que carregar</p>
            </div>
            <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-4 text-center">
              <Shield className="w-8 h-8 text-green-500 mx-auto mb-2" />
              <h4 className="text-white font-semibold text-sm mb-1">Empresas</h4>
              <p className="text-slate-400 text-xs">Sem custo para anunciar<br/>Cargas publicadas gratuitamente</p>
            </div>
            <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-4 text-center">
              <CheckCircle className="w-8 h-8 text-accent-500 mx-auto mb-2" />
              <h4 className="text-white font-semibold text-sm mb-1">Sem Fidelidade</h4>
              <p className="text-slate-400 text-xs">Cancele quando quiser<br/>Sem multas ou taxas</p>
            </div>
          </div>

          <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="text-green-400 font-semibold text-sm mb-1">Vantagem Competitiva</h4>
                <p className="text-slate-300 text-sm">
                  Você só paga quando gerar resultado. Não cobramos assinaturas, mensalidades ou taxas de adesão.
                </p>
              </div>
            </div>
          </div>
        </>
      )
    },
    {
      number: "03",
      title: "Responsabilidades das Partes",
      icon: Shield,
      content: (
        <>
          <p className="mb-4">
            É <strong className="text-white">responsabilidade integral e exclusiva das partes</strong> todo o processo de:
          </p>

          <ul className="space-y-2 mb-4">
            {[
              "Contato inicial e negociação comercial",
              "Formalização de contratos e documentação",
              "Consulta de idoneidade da contraparte",
              "Averbação de cargas e seguros",
              "Procedimentos de segurança e rastreamento",
              "Execução e realização do transporte",
              "Pagamentos e adimplência"
            ].map((item, index) => (
              <li key={index} className="flex items-center gap-3 text-slate-300 text-sm">
                <span className="w-5 h-5 rounded-full bg-green-500/20 flex items-center justify-center text-green-400 text-xs font-bold">✓</span>
                {item}
              </li>
            ))}
          </ul>

          <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="text-red-400 font-semibold text-sm mb-2">Atenção: Cargas não contratadas via Chat/Time BoraFrete</h4>
                <p className="text-slate-300 text-sm mb-2">
                  Para cargas <strong className="text-white">negociadas diretamente</strong> entre as partes (fora dos canais oficiais), a transportadora <strong className="text-white">NÃO SE RESPONSABILIZA</strong> por:
                </p>
                <ul className="text-slate-400 text-xs space-y-1 ml-4 list-disc">
                  <li>Danos materiais à carga</li>
                  <li>Roubos ou furtos</li>
                  <li>Atrasos na entrega</li>
                  <li>Problemas de pagamento</li>
                  <li>Qualquer outro sinistro</li>
                </ul>
              </div>
            </div>
          </div>
        </>
      )
    },
    {
      number: "04",
      title: "Limitação de Responsabilidade",
      icon: Scale,
      content: (
        <>
          <p className="mb-4">
            A BoraFrete <strong className="text-white">apenas disponibiliza o acesso à plataforma</strong> por meio de uma licença de uso, 
            sendo sua responsabilidade limitada <strong className="text-white">exclusivamente a essa atividade</strong>.
          </p>

          <p className="mb-4 text-slate-300">
            A BoraFrete <strong className="text-white">NÃO É PARTE</strong> e <strong className="text-white">NÃO É GARANTIDORA</strong> dos contratos de prestação de serviço entre os Usuários.
          </p>

          <div className="bg-brand-500/10 border border-brand-500/30 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <Scale className="w-5 h-5 text-brand-500 flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="text-brand-400 font-semibold text-sm mb-1">Registro Formal</h4>
                <p className="text-slate-300 text-sm">
                  <strong className="text-white">QUE FIQUE REGISTRADO:</strong> A BoraFrete atua unicamente como divisor entre quem procura carga e quem tem carga. 
                  Não somos intermediários, garantidores ou responsáveis pelas negociações.
                </p>
              </div>
            </div>
          </div>
        </>
      )
    },
    {
      number: "05",
      title: "Obrigações do Usuário",
      icon: FileText,
      content: (
        <>
          <p className="mb-4">Ao utilizar a plataforma, o Usuário compromete-se a:</p>

          <ul className="space-y-2 mb-4">
            {[
              "Fornecer dados verdadeiros, exatos e atualizados",
              "Manter sigilo de login e senha de acesso",
              "Utilizar a plataforma apenas para fins lícitos",
              "Não praticar atos fraudulentos ou de má-fé",
              "Respeitar as legislações de trânsito e transporte",
              "Não utilizar sistemas automatizados (bots, scrapers)",
              "Não reproduzir ou comercializar dados da plataforma"
            ].map((item, index) => (
              <li key={index} className="flex items-center gap-3 text-slate-300 text-sm">
                <span className="w-5 h-5 rounded-full bg-blue-500/20 flex items-center justify-center text-blue-400 text-xs font-bold">✓</span>
                {item}
              </li>
            ))}
          </ul>

          <p className="text-slate-400 text-sm">
            O descumprimento poderá resultar em <strong className="text-white">suspensão ou bloqueio permanente</strong> do cadastro.
          </p>
        </>
      )
    },
    {
      number: "06",
      title: "Propriedade Intelectual",
      icon: Lock,
      content: (
        <>
          <p className="mb-4 text-slate-300">
            Todos os dados de Motoristas, Fretes, Usuários, transações, rotas e registros gerados na Plataforma constituem 
            <strong className="text-white"> ativos exclusivos da BoraFrete</strong>.
          </p>

          <p className="text-slate-300">
            É expressamente vedada a utilização, reprodução, compartilhamento ou comercialização de tais dados para fins próprios ou de terceiros.
          </p>
        </>
      )
    }
  ];

  return (
    <div className="min-h-screen bg-slate-950">
      {/* Header */}
      <div className="bg-gradient-to-b from-brand-900/50 to-slate-950 border-b border-slate-800">
        <div className="max-w-4xl mx-auto px-4 py-12 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-brand-500/10 border border-brand-500/30 text-brand-400 text-sm font-medium mb-6">
            <Scale className="w-4 h-4" />
            Documento Legal
          </div>
          
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Termos de Uso
          </h1>
          
          <p className="text-slate-400 text-lg max-w-2xl mx-auto">
            Condições e responsabilidades do Marketplace de Cargas BoraFrete
          </p>

          <div className="flex items-center justify-center gap-2 mt-6 text-sm text-slate-500">
            <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
            Versão 1.0 • Atualizado em 2024
          </div>
        </div>
      </div>

      {/* Cards de Destaque */}
      <div className="max-w-4xl mx-auto px-4 -mt-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-slate-900/80 backdrop-blur border border-slate-700 rounded-xl p-6 text-center hover:border-brand-500/50 transition-colors">
            <div className="w-12 h-12 bg-gradient-to-br from-brand-500 to-accent-500 rounded-xl flex items-center justify-center mx-auto mb-3">
              <Truck className="w-6 h-6 text-white" />
            </div>
            <h3 className="text-white font-semibold mb-1">Marketplace</h3>
            <p className="text-slate-400 text-sm">Divisor entre quem procura e quem tem carga</p>
          </div>
          
          <div className="bg-slate-900/80 backdrop-blur border border-slate-700 rounded-xl p-6 text-center hover:border-green-500/50 transition-colors">
            <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-emerald-500 rounded-xl flex items-center justify-center mx-auto mb-3">
              <DollarSign className="w-6 h-6 text-white" />
            </div>
            <h3 className="text-white font-semibold mb-1">Sem Mensalidade</h3>
            <p className="text-slate-400 text-sm">Pague apenas pelo que carregar</p>
          </div>
          
          <div className="bg-slate-900/80 backdrop-blur border border-slate-700 rounded-xl p-6 text-center hover:border-blue-500/50 transition-colors">
            <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-500 rounded-xl flex items-center justify-center mx-auto mb-3">
              <Shield className="w-6 h-6 text-white" />
            </div>
            <h3 className="text-white font-semibold mb-1">Segurança</h3>
            <p className="text-slate-400 text-sm">Consulta e averbação por conta das partes</p>
          </div>
        </div>
      </div>

      {/* Conteúdo */}
      <div className="max-w-4xl mx-auto px-4 py-12 space-y-6">
        {sections.map((section, index) => {
          const Icon = section.icon;
          return (
            <section 
              key={index}
              className="bg-slate-900/50 backdrop-blur border border-slate-800 rounded-2xl overflow-hidden hover:border-slate-700 transition-colors"
            >
              <div className="flex items-start gap-4 p-6">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-gradient-to-br from-brand-500 to-brand-600 rounded-xl flex items-center justify-center text-white font-bold text-lg shadow-lg shadow-brand-500/25">
                    {section.number}
                  </div>
                </div>
                
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 mb-3">
                    <Icon className="w-5 h-5 text-brand-500" />
                    <h2 className="text-xl font-bold text-white">{section.title}</h2>
                  </div>
                  
                  <div className="text-slate-300 leading-relaxed">
                    {section.content}
                  </div>
                </div>
              </div>
            </section>
          );
        })}

        {/* Aceite */}
        <div className="bg-gradient-to-r from-brand-500/10 to-accent-500/10 border border-brand-500/30 rounded-2xl p-8 text-center">
          <h3 className="text-2xl font-bold text-white mb-3">
            Concordância
          </h3>
          <p className="text-slate-300 mb-6 max-w-2xl mx-auto">
            Ao utilizar a plataforma BoraFrete, você declara ter lido, compreendido e aceitado 
            integralmente estes Termos de Uso e todas as condições aqui estabelecidas.
          </p>
          
          <Link 
            to="/"
            className="inline-flex items-center gap-2 px-8 py-3 bg-gradient-to-r from-brand-500 to-brand-600 hover:from-brand-600 hover:to-brand-700 text-white font-semibold rounded-xl transition-all shadow-lg shadow-brand-500/25 hover:shadow-brand-500/40"
          >
            <CheckCircle className="w-5 h-5" />
            Voltar para o Início
          </Link>
        </div>

        {/* Footer */}
        <div className="text-center pt-8 border-t border-slate-800">
          <p className="text-slate-500 text-sm">
            BoraFrete Transportes e Logística Ltda.
          </p>
          <p className="text-slate-600 text-xs mt-1">
            © 2024 BoraFrete - Todos os direitos reservados
          </p>
        </div>
      </div>
    </div>
  );
}